import React, { useCallback } from 'react';
import { LandTransporter } from '../../types';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { BaseFormContainer, FormInput } from './common';
import { useFormValidation, commonValidationRules } from '../../hooks/useFormValidation';
// FL-259: TimeSelector, DEFAULT_WORKING_HOURS_SETTINGSは削除（working_days廃止）
// FL-270: StationAutocompleteInputは保持（最寄駅機能）
import { StationAutocompleteInput } from './StationAutocompleteInput';
import { isDemoMode } from '../../utils/environment';
import type { StationDetails } from '@/types/maps';

interface LandTransporterFormData extends Record<string, unknown> {
  name: string;
  phone: string;
  address: string;
  drivableVehicleTypes: string[];
  // FL-259: workingDaysはdriver_schedulesテーブルへ移行済み（UI非表示）
  hasLicenseForLargeVehicles: boolean;
  canWorkNightShift: boolean;
  canWorkLongDistance: boolean;
  hasTemporaryLicense: boolean;
  isATOnly: boolean;
  notes: string;
  // FL-180: 業務制約フラグ
  isSolicitationProhibited: boolean;
  isDesignatedStoreProhibited: boolean;
  // FL-205: マッチング条件フラグ
  can_drive_left_hand?: boolean;
  can_handle_no_inspection?: boolean;
  can_drive_manual?: boolean;
  // FL-270: 最寄駅情報（Google Maps API統合）
  nearestStation?: string;
  nearestStationPlaceId?: string;
  nearestStationLat?: number;
  nearestStationLng?: number;
}

interface LandTransporterFormProps {
  formData: LandTransporterFormData;
  editingTransporter: LandTransporter | null;
  vehicleTypes: Array<{ id: string; name: string }>;
  canEditBusinessConstraints: boolean;
  isLoading?: boolean;
  onInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  onVehicleTypeChange: (vehicleTypeId: string, checked: boolean) => void;
  // FL-259: onWorkingDayToggle, onWorkingTimeChangeはdriver_schedules移行に伴い削除
  // FL-270: onStationSelectは保持（最寄駅機能）
  onStationSelect?: (stationName: string, stationDetails: StationDetails) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export const LandTransporterForm: React.FC<LandTransporterFormProps> = React.memo(
  ({
    formData,
    editingTransporter,
    vehicleTypes,
    canEditBusinessConstraints,
    isLoading = false,
    onInputChange,
    onVehicleTypeChange,
    // FL-259: onWorkingDayToggle, onWorkingTimeChangeは削除
    // FL-270: onStationSelectは保持（最寄駅機能）
    onStationSelect,
    onSubmit,
    onCancel,
  }) => {
    const isDemo = isDemoMode();
    // フォームバリデーション設定
    const validationConfig = {
      name: {
        required: true,
        rules: [commonValidationRules.minLength(1), commonValidationRules.maxLength(50)],
      },
      phone: {
        required: false,
        rules: [commonValidationRules.phone],
      },
      address: {
        required: false,
        rules: [commonValidationRules.minLength(1), commonValidationRules.maxLength(200)],
      },
    };

    const { errors, validateFieldIfTouched } = useFormValidation(validationConfig);

    // 統一フィールド変更ハンドラー
    const handleFieldChange = useCallback(
      (name: string, value: string | number) => {
        const syntheticEvent = {
          target: { name, value },
        } as React.ChangeEvent<HTMLInputElement>;
        onInputChange(syntheticEvent);

        // リアルタイムバリデーション
        validateFieldIfTouched(name, value, formData);
      },
      [onInputChange, validateFieldIfTouched, formData]
    );

    // 非テキスト入力（checkbox等）の処理
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onInputChange(e);
    };

    // FL-270: 最寄駅選択時のハンドラー
    const handleStationSelect = useCallback(
      (stationName: string, stationDetails: StationDetails) => {
        // 親コンポーネントに通知（データベース保存処理は親で実施）
        if (onStationSelect) {
          onStationSelect(stationName, stationDetails);
        }

        // フォームデータに最寄駅情報を設定
        const stationChangeEvent = {
          target: { name: 'nearestStation', value: stationName },
        } as React.ChangeEvent<HTMLInputElement>;
        onInputChange(stationChangeEvent);

        // 駅の座標と Place ID をフォームデータに設定
        // 注: これらのフィールドは隠し項目としてフォームに含める必要がある
        // または親コンポーネントで処理する
      },
      [onInputChange, onStationSelect]
    );

    return (
      <BaseFormContainer
        title={editingTransporter ? '陸送員情報編集' : '新規陸送員登録'}
        icon={<PlusCircleIcon />}
        onSubmit={onSubmit}
        onCancel={onCancel}
        isLoading={isLoading}
        submitLabel={editingTransporter ? '更新' : '登録'}
        isEdit={!!editingTransporter}
        disableSubmit={isDemo && !editingTransporter}
      >
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6'>
          <FormInput
            name='name'
            label='氏名'
            value={formData.name}
            onChange={handleFieldChange}
            placeholder='山田 太郎'
            required
            disabled={isLoading}
            error={errors.name}
          />
          <FormInput
            name='phone'
            label='電話番号'
            value={formData.phone}
            onChange={handleFieldChange}
            placeholder='090-1234-5678'
            disabled={isLoading}
            error={errors.phone}
          />
        </div>
        <FormInput
          name='address'
          label='住所'
          value={formData.address}
          onChange={handleFieldChange}
          placeholder='東京都新宿区西新宿2-8-1'
          disabled={isLoading}
          error={errors.address}
        />

        {/* FL-270: 最寄駅情報（Google Maps API統合） */}
        <StationAutocompleteInput
          id='nearestStation'
          label='最寄駅'
          placeholder='駅名を入力してください（例：新宿駅）'
          value={formData.nearestStation || ''}
          onChange={value => {
            const changeEvent = {
              target: { name: 'nearestStation', value },
            } as React.ChangeEvent<HTMLInputElement>;
            onInputChange(changeEvent);
          }}
          onStationSelect={handleStationSelect}
          disabled={isLoading}
          className='sm:col-span-2'
        />

        {/* 運転可能車種 */}
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
            運転可能車種 *
          </label>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
            {vehicleTypes.map(vehicleType => (
              <label
                key={vehicleType.id}
                className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'
              >
                <input
                  type='checkbox'
                  checked={formData.drivableVehicleTypes.includes(vehicleType.name)}
                  onChange={e => onVehicleTypeChange(vehicleType.name, e.target.checked)}
                  className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
                />
                <span className='ml-3 text-sm text-gray-700 dark:text-gray-300'>
                  {vehicleType.name}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* FL-259: 勤務スケジュール廃止 - driver_schedulesテーブルへ移行 */}

        {/* その他のフラグ */}
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
            その他の条件
          </label>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <label className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'>
              <input
                type='checkbox'
                name='hasLicenseForLargeVehicles'
                checked={formData.hasLicenseForLargeVehicles}
                onChange={handleCheckboxChange}
                className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
              />
              <span className='ml-3 text-sm text-gray-700 dark:text-gray-300'>
                大型車運転免許保有
              </span>
            </label>
            <label className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'>
              <input
                type='checkbox'
                name='canWorkNightShift'
                checked={formData.canWorkNightShift}
                onChange={handleCheckboxChange}
                className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
              />
              <span className='ml-3 text-sm text-gray-700 dark:text-gray-300'>夜間勤務可能</span>
            </label>
            <label className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'>
              <input
                type='checkbox'
                name='canWorkLongDistance'
                checked={formData.canWorkLongDistance}
                onChange={handleCheckboxChange}
                className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
              />
              <span className='ml-3 text-sm text-gray-700 dark:text-gray-300'>長距離運転可能</span>
            </label>
            <label className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'>
              <input
                type='checkbox'
                name='hasTemporaryLicense'
                checked={formData.hasTemporaryLicense}
                onChange={handleCheckboxChange}
                className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
              />
              <span className='ml-3 text-sm text-gray-700 dark:text-gray-300'>
                仮免許での対応可能
              </span>
            </label>
            <label className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'>
              <input
                type='checkbox'
                name='isATOnly'
                checked={formData.isATOnly}
                onChange={handleCheckboxChange}
                className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
              />
              <span className='ml-3 text-sm text-gray-700 dark:text-gray-300'>
                オートマ車のみ対応
              </span>
            </label>
            {/* FL-205: マッチング条件フラグ */}
            <label className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'>
              <input
                type='checkbox'
                name='can_drive_left_hand'
                checked={formData.can_drive_left_hand ?? true}
                onChange={handleCheckboxChange}
                className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
              />
              <span className='ml-3 text-sm text-gray-700 dark:text-gray-300'>
                左ハンドル対応可
              </span>
            </label>
            <label className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'>
              <input
                type='checkbox'
                name='can_handle_no_inspection'
                checked={formData.can_handle_no_inspection ?? true}
                onChange={handleCheckboxChange}
                className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
              />
              <span className='ml-3 text-sm text-gray-700 dark:text-gray-300'>車検なし対応可</span>
            </label>
            <label className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'>
              <input
                type='checkbox'
                name='can_drive_manual'
                checked={formData.can_drive_manual ?? true}
                onChange={handleCheckboxChange}
                className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
              />
              <span className='ml-3 text-sm text-gray-700 dark:text-gray-300'>MT運転可能</span>
            </label>
          </div>
        </div>

        {/* FL-180: 業務制約（権限チェック） */}
        {canEditBusinessConstraints && (
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
              業務制約
            </label>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <label className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'>
                <input
                  type='checkbox'
                  name='isSolicitationProhibited'
                  checked={formData.isSolicitationProhibited}
                  onChange={handleCheckboxChange}
                  className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
                />
                <span
                  className='ml-3 text-sm text-gray-700 dark:text-gray-300'
                  aria-label='陸送員の客引き行為を禁止する'
                >
                  客引き行為NG
                </span>
              </label>
              <label className='flex items-center p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] touch-target'>
                <input
                  type='checkbox'
                  name='isDesignatedStoreProhibited'
                  checked={formData.isDesignatedStoreProhibited}
                  onChange={handleCheckboxChange}
                  className='rounded border-gray-300 text-primary-600 focus:ring-green-500 min-h-[20px] min-w-[20px]'
                />
                <span
                  className='ml-3 text-sm text-gray-700 dark:text-gray-300'
                  aria-label='陸送員の指定店舗への配送を禁止する'
                >
                  指定店舗への配送NG
                </span>
              </label>
            </div>
          </div>
        )}

        {/* 特記事項 */}
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
            特記事項
          </label>
          <textarea
            name='notes'
            value={formData.notes}
            onChange={e => handleFieldChange('notes', e.target.value)}
            placeholder='その他の特記事項があれば入力してください'
            disabled={isLoading}
            rows={3}
            className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed'
          />
        </div>
      </BaseFormContainer>
    );
  }
);

LandTransporterForm.displayName = 'LandTransporterForm';

export default LandTransporterForm;
